import React, { useMemo } from "react";
import Card from "./Card";
import { ORANGE } from "./labels";
import { formatStackBB, formatAmount, translateDescrPL } from "./helpers";

function fmtSignedAmount(value, stackViewMode, bbAmount) {
  const x = Number(value || 0);
  if (!x) return null;
  const sign = x > 0 ? "+" : "−";
  const abs = Math.abs(x);

  if (stackViewMode === "bb" && Number(bbAmount) > 0) {
    return `${sign}${formatStackBB(abs, bbAmount)} BB`;
  }
  return `${sign}${abs}`;
}

export default function SeatPanelCard({
  // stack/amount display
  stackViewMode = "chips",
  bbAmount = 0,

  style,
  outerRef,
  seatNo,
  isDark,
  isPlaceholder,

  displayName,
  isDealer,
  // ✅ winner highlight
  isWinner = false,
  isSmallBlind,
  isBigBlind,
  isAction,

  folded,
  inHand,
  stack,
  committedStreet,
  lastActionText,
  allIn,

  isMe,
  hideMyHoleInSeat,
  showMyCards,
  myCards,
  hasPublic,
  publicCards,
  seatCardSize,

  showShowBtn,
  onShowCards,

  widthPx,
  background,
  border,
  shadow,

  layoutVariant = "twoColumn",
  allowActionWrap = false,
  handDealt = false,

  // ✅ NEW: end-of-hand result
  showHandResult = false,
  handDescr,     // string
  handDelta = 0, // number +/- (netto)
}) {
  const labelCommitted = widthPx < 132 ? "Wkład" : "W licytacji";

  const showDealtCards = !!handDealt && !!inHand;

  const formatActionText = (txt) => {
    if (typeof txt !== "string" || !txt.trim()) return "—";
    if (stackViewMode !== "bb" || !bbAmount || bbAmount <= 0) return txt;

    const m = txt.trim().match(/^(.+?)\s+(\d+(?:[.,]\d+)?)$/);
    if (!m) return txt;

    const prefix = m[1];
    const raw = m[2].replace(",", ".");
    const amountChips = Number(raw);
    if (!Number.isFinite(amountChips)) return txt;

    return `${prefix} ${formatStackBB(amountChips, bbAmount)} BB`;
  };

  const actionText = formatActionText(lastActionText);
  const isActionLong = actionText.length >= 14;

  const autoStacked = useMemo(() => {
    if (!inHand) return false;

    const cardMax =
      seatCardSize === "xs" ? 60 :
      seatCardSize === "sm" ? 66 :
      seatCardSize === "md" ? 72 :
      seatCardSize === "xxs" ? 44 :
      44;

    const cardsRowNeed = cardMax * 2 + 6;
    const textColNeed = 86;

    return Number(widthPx || 0) < cardsRowNeed + textColNeed;
  }, [inHand, widthPx, seatCardSize]);

  const isStacked = layoutVariant === "stacked" || autoStacked;

  const hasRightCol = !!inHand && !isStacked;
  const actionBelow = hasRightCol && widthPx < 170 && isActionLong;

  const actionChipStyle = {
    color: ORANGE,
    padding: "4px 8px",
    borderRadius: 10,
    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
    border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.2,

    whiteSpace: isStacked || allowActionWrap ? "normal" : "nowrap",
    overflow: isStacked || allowActionWrap ? "visible" : "hidden",
    textOverflow: isStacked || allowActionWrap ? "clip" : "ellipsis",
    wordBreak: isStacked || allowActionWrap ? "break-word" : "normal",

    opacity: lastActionText ? 1 : 0.35,
  };

  const emptyCardsContent = (
    <>
      <EmptyCard size={seatCardSize} />
      <EmptyCard size={seatCardSize} />
    </>
  );

  const flipMs = 900;
  const flipGapMs = 160;

  const dealMs = 900;
  const dealGapMs = 160;

  const dealtDownCards = (
    <>
      <Card faceDown size={seatCardSize} revealMode="wipeUp" revealMs={dealMs} revealDelayMs={0} />
      <Card faceDown size={seatCardSize} revealMode="wipeUp" revealMs={dealMs} revealDelayMs={dealGapMs} />
    </>
  );

  const cardsContent = (
    <>
      {isMe ? (
        hideMyHoleInSeat && !hasPublic ? (
          <>{dealtDownCards}</>
        ) : showMyCards ? (
          <>
            <Card c={myCards[0]} size={seatCardSize} revealMode="flip" revealMs={flipMs} revealDelayMs={0} />
            <Card c={myCards[1]} size={seatCardSize} revealMode="flip" revealMs={flipMs} revealDelayMs={flipGapMs} />
          </>
        ) : (
          <>{dealtDownCards}</>
        )
      ) : hasPublic ? (
        <>
          <Card c={publicCards[0]} size={seatCardSize} revealMode="flip" revealMs={flipMs} revealDelayMs={0} />
          <Card c={publicCards[1]} size={seatCardSize} revealMode="flip" revealMs={flipMs} revealDelayMs={flipGapMs} />
        </>
      ) : (
        <>{dealtDownCards}</>
      )}
    </>
  );

  const resultText = showHandResult && handDescr ? translateDescrPL(handDescr) : null;
  const resultDeltaTxt = showHandResult ? fmtSignedAmount(handDelta, stackViewMode, bbAmount) : null;
  const showResultLine = !!resultText || !!resultDeltaTxt;

  const effectiveBorder = showHandResult && isWinner ? "2px solid rgba(80,220,140,0.90)" : border;
  const effectiveShadow = showHandResult && isWinner ? "0 0 0 1px rgba(80,220,140,0.22) inset, 0 0 22px rgba(80,220,140,0.18)" : shadow;

  return (
    <div
      ref={outerRef}
      key={seatNo}
      style={{
        ...style,
        width: widthPx,
        padding: "8px 8px 7px",
        borderRadius: 14,
        background,
        border: effectiveBorder,
        boxShadow: effectiveShadow,
        color: isDark ? "#fff" : "#000",
        opacity: isPlaceholder ? 0.42 : folded ? 0.62 : 1,
        pointerEvents: isMe ? "auto" : "none",
        zIndex: isMe ? 20 : 10,
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
        <div
          style={{
            fontWeight: 900,
            fontSize: 13,
            lineHeight: 1.05,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
          title={`${displayName} (${seatNo})`}
        >
          {displayName} <span style={{ opacity: 0.75 }}>({seatNo})</span>
        </div>

        <div style={{ display: "flex", gap: 6, alignItems: "center", flex: "0 0 auto" }}>
          {isDealer ? <Badge text="D" /> : null}
          {isSmallBlind ? <Badge text="SB" /> : null}
          {isBigBlind ? <Badge text="BB" /> : null}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: hasRightCol ? "1fr auto" : "1fr",
          columnGap: hasRightCol ? 6 : 0,
          rowGap: 6,
          alignItems: "start",
          marginTop: 8,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, opacity: 0.75, lineHeight: 1 }}>Stack</div>
          <div style={{ fontSize: 16, fontWeight: 900, lineHeight: 1 }}>
            {formatAmount(stack, stackViewMode, bbAmount)}
          </div>
        </div>

        {inHand || isPlaceholder ? (
          isStacked ? (
            <div style={{ gridColumn: 1, display: "flex", gap: 6, justifyContent: "flex-start", marginTop: 2 }}>
              {showDealtCards ? cardsContent : emptyCardsContent}
            </div>
          ) : (
            <div style={{ gridRow: "1 / span 4", gridColumn: 2, display: "flex", gap: 6, justifyContent: "flex-end" }}>
              {showDealtCards ? cardsContent : emptyCardsContent}
            </div>
          )
        ) : null}

        {!actionBelow ? (
          <div style={{ minWidth: 0 }}>
            <div style={actionChipStyle} title={typeof lastActionText === "string" ? lastActionText : ""}>
              {actionText}
            </div>
          </div>
        ) : null}

        <div style={{ minWidth: 0, fontSize: 12, opacity: 0.88, lineHeight: 1, whiteSpace: "nowrap" }}>
          <span style={{ color: ORANGE, fontWeight: 900 }}>
            {labelCommitted}: {formatAmount(committedStreet, stackViewMode, bbAmount)}
          </span>
        </div>

        {/* ✅ wynik rozdania */}
        {showResultLine ? (
          <div style={{ minWidth: 0, fontSize: 12, opacity: 0.92, lineHeight: 1.15 }}>
            {resultText ? (
              <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                Układ: <b>{resultText}</b>
              </div>
            ) : null}
            {resultDeltaTxt ? <div><b style={{ color: ORANGE }}>{resultDeltaTxt}</b></div> : null}
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {allIn ? <MiniTag text="ALL-IN" /> : null}
          {folded ? <MiniTag text="FOLD" danger /> : null}
        </div>
      </div>

      {actionBelow ? (
        <div style={{ marginTop: 6, ...actionChipStyle }} title={typeof lastActionText === "string" ? lastActionText : ""}>
          {actionText}
        </div>
      ) : null}

      {showShowBtn ? (
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onShowCards?.()}
            style={{ pointerEvents: "auto", padding: "6px 10px", fontSize: 12 }}
            title="Pokaż swoje karty wszystkim (po zakończeniu rozdania)."
          >
            Pokaż karty
          </button>
        </div>
      ) : null}
    </div>
  );
}

function EmptyCard({ size }) {
  const dims =
    size === "xs" ? { w: 60, h: 82 } :
    size === "sm" ? { w: 66, h: 90 } :
    size === "md" ? { w: 72, h: 98 } :
    size === "xxs" ? { w: 44, h: 62 } :
    { w: 44, h: 62 };

  return (
    <div
      style={{
        width: dims.w,
        height: dims.h,
        borderRadius: 12,
        border: "1px solid rgba(255,165,0,0.35)",
        background: "rgba(0,0,0,0.10)",
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.25)",
        opacity: 0.55,
      }}
    />
  );
}

function Badge({ text, strong }) {
  const orange = "rgba(255,165,0,0.95)";
  return (
    <span
      style={{
        fontSize: 11,
        padding: "2px 7px",
        borderRadius: 999,
        border: `1px solid ${orange}`,
        background: strong ? "rgba(255,165,0,0.16)" : "rgba(255,165,0,0.10)",
        color: orange,
        fontWeight: strong ? 900 : 800,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
}

function MiniTag({ text, danger }) {
  return (
    <span
      style={{
        fontSize: 10,
        padding: "1px 6px",
        borderRadius: 999,
        border: danger ? "1px solid rgba(255,80,80,0.55)" : "1px solid rgba(255,255,255,0.14)",
        background: danger ? "rgba(255,80,80,0.10)" : "rgba(255,255,255,0.06)",
        color: danger ? "rgba(255,120,120,0.95)" : "rgba(255,255,255,0.85)",
        fontWeight: 900,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
}